/**
 * Current-Week drilldown modal.
 *
 * Each tile / chart-segment click on the Current Week sub-tab opens this with
 * a starting ``mode``. For segments that belong to a category GROUP (the
 * Deliveries-by-Day bars, the OTD ring, the OTIF ring) the modal shows a TAB
 * BAR for every sibling category — On time / Early / Late / Pending, or
 * Passed / Pending / Failed — so the user can step through the specifics of
 * each without re-opening. Single-metric tiles open without tabs.
 *
 * Param building lives in ``currentWeekDrilldownParams.ts``; the table body
 * (with the two judging dates per row) in ``CurrentWeekDrilldownTable.tsx``.
 */
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Loader2, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import { purchasingAPI } from '../../../lib/api';
import type { POLineSchedule, GoodsReceiptNote } from '../../../lib/api';
import apiClient from '../../../lib/api/client';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useMenuSettings } from '../../../contexts/MenuSettingsContext';
import OtifOverrideDialog from '../../purchasing/OtifOverrideDialog';
import ErpMismatchRepairModal from '../../../pages/admin/ErpMismatchRepairModal';
import {
  buildCurrentWeekParams,
  MODE_TITLES,
  getEntityForMode,
  getTabGroup,
  tabKey,
  type CurrentWeekDrilldownMode,
  type CurrentWeekDrilldownFilters,
  type DrilldownParamShape,
  type DrilldownEntity,
  type DrilldownTab,
} from './currentWeekDrilldownParams';
import {
  CurrentWeekDrilldownTable,
  SCHEDULE_SORT_FIELDS,
  GRN_SORT_FIELDS,
} from './CurrentWeekDrilldownTable';
import { OtifWindowUnavailableNote } from './OtifWindowUnavailableNote';

export type { CurrentWeekDrilldownMode, CurrentWeekDrilldownFilters };

interface CurrentWeekDrilldownModalProps {
  open: boolean;
  onClose: () => void;
  mode: CurrentWeekDrilldownMode;
  filters: CurrentWeekDrilldownFilters;
  /** Sun → today (cost / quality bucket window). */
  weekStartIso: string;
  weekEndIso: string;
  /** Sun → Thu/today (delivery bucket — frozen rule). */
  otifWindowStartIso: string;
  otifWindowEndIso: string;
  /** Today (used for arrears: schedule_date < today). */
  todayIso: string;
  /** Optional ISO date for "by day" stacked-bar drilldowns. */
  dayIso?: string;
  /** Arrears grace buffer (days) — arrears cutoff = today - buffer. */
  arrearsBufferDays?: number;
}

const PAGE_SIZE = 50;

function buildPageList(current: number, total: number): Array<number | '…'> {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out: Array<number | '…'> = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) out.push('…');
  for (let p = start; p <= end; p++) out.push(p);
  if (end < total - 1) out.push('…');
  out.push(total);
  return out;
}

type AnyRow = POLineSchedule | GoodsReceiptNote;

const entityDefaultSort = (e: DrilldownEntity) => (e === 'grn' ? 'receipt_date' : 'schedule_date');

export function CurrentWeekDrilldownModal({
  open, onClose, mode, filters,
  weekStartIso, weekEndIso, otifWindowStartIso, otifWindowEndIso,
  todayIso, dayIso, arrearsBufferDays = 0,
}: CurrentWeekDrilldownModalProps) {
  const { formatDate } = useFormatters();
  const { user } = useAuth();
  const { isFeatureEnabled } = useMenuSettings();
  // Staff/superuser may manually override a schedule's OTIF verdict —
  // same population that operates GRN Repair; backend enforces it too.
  const canOtifOverride = !!user?.is_superuser || !!user?.is_staff;
  const [overrideTarget, setOverrideTarget] = useState<POLineSchedule | null>(null);
  // ERP Balance beside Open Qty, for customer-side users on a tenant whose
  // import captures it (same rule as the Schedules tab). The real arrear is a
  // past slot the ERP still reports open; staff/superusers get a Repair button
  // where Helix disagrees, opening the same GRN repair board as the ERP Monitor.
  const isSupplier = user?.platform === 'SUPPLIER';
  const showErpBalance = !isSupplier && user?.import_handler === 'dennis_eagle';
  // CR-6274: the Repair button opens the GRN repair board, so it follows the
  // GRN Repair module switch. The OTIF override above is a separate feature
  // that happens to share the same staff population, and is deliberately NOT
  // gated by it.
  const canRepair = showErpBalance && canOtifOverride && isFeatureEnabled('grn_repair');
  const [repairTarget, setRepairTarget] = useState<{ poLineId: number; label: string } | null>(null);
  // Post-override refetches must skip the 60s Cache-Control browser cache on
  // the schedules list, or the just-overridden row replays with its stale
  // verdict. A ref (not state) so setting it doesn't reset pagination.
  const cacheBustRef = useRef<number | null>(null);

  // The active category — starts at the clicked mode, switchable via the tab bar.
  // ``activeOverlay`` carries the extra param filter for KPI-tile groups whose
  // tabs share a mode (OTIF window → otif_status slices).
  const [activeMode, setActiveMode] = useState<CurrentWeekDrilldownMode>(mode);
  const [activeOverlay, setActiveOverlay] = useState<Record<string, string> | undefined>(undefined);
  const entity: DrilldownEntity = useMemo(() => getEntityForMode(activeMode), [activeMode]);
  const tabGroup = useMemo(() => getTabGroup(activeMode), [activeMode]);
  const activeKey = useMemo(
    () => tabKey({ mode: activeMode, label: '', overlay: activeOverlay }),
    [activeMode, activeOverlay],
  );

  const [rows, setRows] = useState<AnyRow[]>([]);
  // CR-7714: the site's OTIF window could not be loaded for this request.
  const [windowUnavailable, setWindowUnavailable] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageInput, setPageInput] = useState<string>('1');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [sortCol, setSortCol] = useState<string>(entityDefaultSort(entity));
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const sortFields = entity === 'grn' ? GRN_SORT_FIELDS : SCHEDULE_SORT_FIELDS;
  const defaultSort = entityDefaultSort(entity);

  const orderingParam = useMemo(() => {
    const field = sortFields[sortCol];
    if (!field) return defaultSort;
    return sortDir === 'desc' ? `-${field}` : field;
  }, [sortCol, sortDir, sortFields, defaultSort]);

  const baseParams = useMemo(
    () => ({
      ...buildCurrentWeekParams({
        mode: activeMode, filters,
        weekStartIso, weekEndIso,
        otifStartIso: otifWindowStartIso,
        otifEndIso: otifWindowEndIso,
        todayIso, dayIso, arrearsBufferDays,
      }),
      ...(activeOverlay ?? {}),
    }),
    [activeMode, activeOverlay, filters, weekStartIso, weekEndIso, otifWindowStartIso, otifWindowEndIso, todayIso, dayIso, arrearsBufferDays],
  );

  // Only the newest request may write to state. Two effects below can fire in
  // the same commit — the reset-to-page-1 and the fetch-current-page — and the
  // two responses come back in whatever order the network decides. Without
  // this, a slower page-1 reply lands after the page-2 reply you asked for and
  // the table silently snaps back, which reads as "paging does nothing".
  const requestSeqRef = useRef(0);

  const fetchPage = useCallback(async (targetPage: number) => {
    const seq = ++requestSeqRef.current;
    setLoading(true);
    setError(null);
    try {
      const params: DrilldownParamShape = {
        ...baseParams, page: targetPage, page_size: PAGE_SIZE, ordering: orderingParam,
        ...(cacheBustRef.current ? { _t: cacheBustRef.current } : {}),
      };
      const res = entity === 'grn'
        ? await purchasingAPI.getGRNs({ ...params, all_suppliers: true })
        : await purchasingAPI.getSchedules({ ...params, all_suppliers: true });
      if (seq !== requestSeqRef.current) return;   // superseded
      setRows((res.data.results ?? []) as AnyRow[]);
      setWindowUnavailable(!!(res.data as { otif_window_unavailable?: boolean }).otif_window_unavailable);
      setTotal(res.data.count ?? 0);
    } catch (err: unknown) {
      if (seq !== requestSeqRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load drilldown');
      setRows([]);
      setWindowUnavailable(false);
      setTotal(0);
    } finally {
      // Only the newest request owns the spinner; an overtaken one clearing it
      // would blank the loading state while the real fetch is still running.
      if (seq === requestSeqRef.current) setLoading(false);
    }
  }, [baseParams, orderingParam, entity]);

  // Identity of the QUERY, as opposed to identity of the fetch callback. The
  // callback is rebuilt whenever the parent re-renders with a fresh filters
  // object, which is not a reason to throw the user back to page 1.
  const queryKey = useMemo(
    () => JSON.stringify([baseParams, orderingParam, entity]),
    [baseParams, orderingParam, entity],
  );

  // Sync the clicked mode into state whenever the modal (re)opens or the
  // parent passes a new starting mode.
  useEffect(() => {
    if (!open) return;
    setActiveMode(mode);
    setActiveOverlay(undefined);
    setSortCol(entityDefaultSort(getEntityForMode(mode)));
    setSortDir('asc');
  }, [open, mode]);

  // A different query means the old page number is meaningless — page 7 of
  // Passed is not page 7 of Failed — so go back to the start. Keyed on the
  // query, NOT on fetchPage: keying on the callback meant any parent re-render
  // reset the page under the user mid-browse.
  useEffect(() => {
    if (!open) return;
    setPage(1);
    setPageInput('1');
  }, [open, queryKey]);

  // The single fetcher: whatever page is current, for whatever query is
  // current. One effect rather than two removes the double-fire; the sequence
  // guard in fetchPage covers the commit where page and query change together.
  useEffect(() => {
    if (!open) return;
    fetchPage(page);
    // fetchPage is intentionally absent: queryKey already captures everything
    // it closes over, and including it would re-fire on every re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, queryKey, page]);

  useEffect(() => { setPageInput(String(page)); }, [page]);

  const goToPage = useCallback((p: number) => {
    if (p < 1 || p > totalPages || p === page || loading) return;
    setPage(p);
  }, [page, totalPages, loading]);

  const selectTab = useCallback((t: DrilldownTab) => {
    if (tabKey(t) === activeKey) return;
    setActiveMode(t.mode);
    setActiveOverlay(t.overlay);
    setSortCol(entityDefaultSort(getEntityForMode(t.mode)));
    setSortDir('asc');
  }, [activeKey]);

  const handleHeaderClick = useCallback((colId: string) => {
    if (!sortFields[colId]) return;
    if (sortCol === colId) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortCol(colId); setSortDir('asc'); }
  }, [sortCol, sortFields]);

  const exportSlug = useMemo(() => {
    const map: Record<CurrentWeekDrilldownMode, string> = {
      due_this_week: 'schedules_due_this_week', open_this_week: 'open_schedules_this_week',
      received_this_week: 'received_schedules_this_week', arrears: 'arrears',
      otif_this_week: 'otif_window', otd_this_week: 'otd_window',
      day_on_time: 'on_time_deliveries', day_early: 'early_deliveries',
      day_late: 'late_deliveries', day_pending: 'pending_schedules',
      mix_on_time: 'on_time_deliveries_wtd', mix_early: 'early_deliveries_wtd',
      mix_late: 'late_deliveries_wtd', mix_otif_pass: 'otif_passed_wtd',
      mix_otif_pending: 'otif_pending_wtd', mix_otif_fail: 'otif_failed_wtd',
    };
    const base = map[activeMode] ?? activeMode;
    if (dayIso) return `${base}_${dayIso}`;
    return `${base}_${new Date().toISOString().slice(0, 10)}`;
  }, [activeMode, dayIso]);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const exportParams: DrilldownParamShape = { ...baseParams, ordering: orderingParam };
      const qs = new URLSearchParams(
        Object.entries(exportParams)
          .filter(([, v]) => v !== undefined && v !== null && v !== '')
          .map(([k, v]) => [k, String(v)]),
      ).toString();
      const path = entity === 'grn'
        ? `/purchasing/grns/export-xlsx/?${qs}`
        : `/purchasing/schedules/export-xlsx/?${qs}`;
      const resp = await apiClient.get(path, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${exportSlug}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Excel export ready');
    } catch (err: unknown) {
      console.error('Drilldown Excel export failed:', err);
      toast.error('Failed to export Excel');
    } finally {
      setExporting(false);
    }
  }, [baseParams, orderingParam, entity, exportSlug]);

  const pageButtons = useMemo(() => buildPageList(page, totalPages), [page, totalPages]);

  const dayLabel = useMemo(() => {
    if (!dayIso) return null;
    // eslint-disable-next-line no-restricted-syntax -- CR-6307: deliberately UTC-pinned weekday label
    return new Date(dayIso + 'T00:00:00Z').toLocaleDateString(undefined, { weekday: 'short', timeZone: 'UTC' });
  }, [dayIso]);

  const noun = entity === 'grn' ? 'delivery' : 'schedule';
  const nounPlural = entity === 'grn' ? 'deliveries' : 'schedules';
  const emptyText = entity === 'grn' ? 'No deliveries match this category.' : 'No schedules match this category.';

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="!max-w-[min(1480px,95vw)] w-[min(1480px,95vw)]">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <DialogTitle>{MODE_TITLES[activeMode]}</DialogTitle>
              <DialogDescription>
                {total.toLocaleString()} {total === 1 ? noun : nounPlural}
                {dayIso ? <> · {dayLabel ? `${dayLabel} ` : ''}{formatDate(dayIso)}</> : null}
                {filters.part_number ? <> · part <span className="font-mono">{filters.part_number}</span></> : null}
                {filters.supplier_id ? <> · supplier #{filters.supplier_id}</> : null}
                {filters.erp_order_type === '1' ? <> · spot orders</> : <> · scheduling agreements</>}
              </DialogDescription>
            </div>
            <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting || total === 0}>
              {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Export to Excel
            </Button>
          </div>
        </DialogHeader>

        {/* Category tabs — only for segment groups (day bars / OTD ring / OTIF ring). */}
        {tabGroup && (
          <div className="flex flex-wrap gap-1 border-b pb-2">
            {tabGroup.map((t) => {
              const active = tabKey(t) === activeKey;
              return (
                <button
                  key={tabKey(t)}
                  onClick={() => selectTab(t)}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                    active
                      ? 'bg-primary text-primary-foreground font-medium'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                  aria-current={active ? 'true' : undefined}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        )}

        {error && <div className="text-sm text-red-600 py-3">{error}</div>}

        <OtifWindowUnavailableNote show={windowUnavailable} />
        {loading && rows.length === 0 ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-muted-foreground py-10 text-center">{emptyText}</div>
        ) : (
          <CurrentWeekDrilldownTable
            entity={entity}
            rows={rows}
            sortCol={sortCol}
            sortDir={sortDir}
            onHeaderClick={handleHeaderClick}
            onOtifOverride={canOtifOverride ? setOverrideTarget : undefined}
            showErpBalance={showErpBalance}
            onRepair={canRepair
              ? (r) => setRepairTarget({ poLineId: r.po_line_id, label: `${r.po_number}/${r.line_number}` })
              : undefined}
          />
        )}

        {/* Staff-only manual OTIF override — refetches the current page so the
            row's verdict pill and the tab counts update in place. */}
        <OtifOverrideDialog
          open={overrideTarget !== null}
          schedule={overrideTarget}
          onClose={() => setOverrideTarget(null)}
          onDone={() => { cacheBustRef.current = Date.now(); fetchPage(page); }}
        />

        {/* Staff-only GRN repair for a slot whose Helix balance differs from the
            ERP's — the ERP Monitor's board. Refetches past the browser cache so
            the repaired balances show without waiting out the 60s cache. */}
        <ErpMismatchRepairModal
          poLineId={repairTarget?.poLineId ?? null}
          label={repairTarget?.label ?? ''}
          onClose={() => setRepairTarget(null)}
          onChanged={() => { cacheBustRef.current = Date.now(); fetchPage(page); }}
        />

        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between gap-2 pt-2 flex-wrap">
            <div className="flex items-center gap-1">
              <Button size="sm" variant="outline" disabled={page <= 1 || loading} onClick={() => goToPage(page - 1)} aria-label="Previous page">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              {pageButtons.map((p, idx) => (
                p === '…' ? (
                  <span key={`gap-${idx}`} className="px-2 text-xs text-muted-foreground">…</span>
                ) : (
                  <Button
                    key={p} size="sm"
                    variant={p === page ? 'default' : 'outline'}
                    disabled={loading}
                    onClick={() => goToPage(p)}
                    aria-current={p === page ? 'page' : undefined}
                    className="min-w-[2.25rem]"
                  >
                    {p}
                  </Button>
                )
              ))}
              <Button size="sm" variant="outline" disabled={page >= totalPages || loading} onClick={() => goToPage(page + 1)} aria-label="Next page">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <label htmlFor="cw-page-jump">Go to page</label>
              <input
                id="cw-page-jump"
                type="number" min={1} max={totalPages}
                value={pageInput} disabled={loading}
                onChange={(e) => setPageInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const n = parseInt(pageInput, 10);
                    if (!Number.isNaN(n)) goToPage(Math.max(1, Math.min(totalPages, n)));
                  }
                }}
                onBlur={() => {
                  const n = parseInt(pageInput, 10);
                  if (!Number.isNaN(n)) goToPage(Math.max(1, Math.min(totalPages, n)));
                  else setPageInput(String(page));
                }}
                className="h-7 w-16 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300"
              />
              <span>of {totalPages.toLocaleString()}</span>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
